// La caja de prompt del modo Redes — ADR-2026-10-01-modo-redes.
//
// Es `PromptBoxLibre` (la misma caja de Prompt libre y del Creador) metida en un marco
// propio, con el prompt a todo el ancho y el Generar plano al final de la fila de
// opciones. A la izquierda del marco, el interruptor Imagen/Video en vertical. Producto
// y modelo entran por las TARJETAS de arriba de la caja (Añadir producto, Añadir modelo, Añadir referencia:
// el selector del Agente, cada una en su pestaña), que muestran lo elegido en miniatura. Debajo del prompt, las opciones como
// pastillas —Añadir producto, Añadir modelo, Añadir referencia, Configuración, Cantidad o Tiempo, Prompts,
// Recientes, Ayuda de prompt—; cada una abre su panel portaleado sobre el botón, como los
// menús del composer (ADR-2026-09-20-la-fila-del-composer-scrollea-y-sus-menus-se-portalean).
//
// El marco: sin borde marcado y con una sombra larga por debajo, que es lo que se pidió
// ("menos bordes y más sombra"). El anillo azul sólo con el foco adentro.

import React, { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { bajadaCortaModo, nombreCortoModo, rutaDeModo } from '../../modesCatalog';
import type { RubroDeCrear } from './contenidoPorRubro';
import { ArrowUp, BookOpen, Check, ChevronRight, Clapperboard, Image as ImageIcon, Loader2, MessageCircle, Minus, Package, Plus, Settings2, Sparkles, Square, Upload, User, Wand2, FlaskConical, X } from 'lucide-react';
import { useEsEscritorio } from '../../../../chat-atlas/ui/useEsEscritorio';
import { CumbreQueAsoma } from './CumbreQueAsoma';
import './crear.css';
import type { ChatAttachment } from '../../agentAttachments';
import { FORMAT_OPTIONS_V2 } from '../../mockData';
import { useUserProducts } from '../../productsContext';
import { useUserModels } from '../../modelsContext';
import { TarjetaDeReferencia } from './TarjetaDeReferencia';
import { usePosicionSobreElBoton } from '../../components/posicionSobreElBoton';
import { creditosPorImagenTexto, OPCIONES_RESOLUCION, type ResolucionImagen } from '../../../../lib/resolucionImagen';
import { AYUDA_BORRADOR, RESOLUCIONES_VIDEO, esBorrador } from '../../../../lib/resolucionVideo';
import { EtiquetaBorrador } from '../../../../components/EtiquetaBorrador';
import { videoGeneralRatePerSec, type VideoResolution } from '../../../../utils/credits';
import type { IdDeFuente } from '../../../../chat-atlas/ui/ModalDeFuentes';
import { PromptBoxLibre } from '../promptLibre/PromptBoxLibre';
import { MAX_CANTIDAD, VIDEO_DURATION_MIN, videoDuracionMax, type VideoQuality } from '../asistente/directMode';
import { TipoDeVideoAnclado } from '../creadorVideo/TipoDeVideoPill';
import { conCalidad } from '../creadorVideo/creadorVideo';
import { palabraDeModeloActual } from '../../../../lib/nombreDeModelo';
import { BibliotecaDePrompts } from './BibliotecaDePrompts';
import WakeSlider from '../../../../components/WakeSlider';
import StatusMark, { type EstadoDeMarca } from '../../../../components/StatusMark';
import { AvisosDeVideos } from '../../../../chat-atlas/ui/AvisosDeVideos';
import { useVerVideoAdjunto } from '../../../../chat-atlas/ui/useVerVideoAdjunto';
import { FormatosPersonalizados } from '../../components/FormatosPersonalizados';
import { useFormatosPersonalizados } from '../../../../utils/formatosPersonalizadosStore';
import { PROVEEDORES_DE_IMAGEN, etiquetaProveedorDeImagen, type ProveedorDeImagen } from '../../../../lib/proveedoresDeImagen';
import {
  DURACION_MEDIA_DE_MEJORA_MS,
  creditosDeRedes,
  formatoActivo,
  placeholderDeRedes,
  ANIMAR_MAX_SEG,
  ANIMAR_MIN_SEG,
  MODOS_DE_CREAR,
  conMedio,
  conModo,
  duracionDeAnimar,
  esAnimar,
  esAutomatico,
  type ModoDeCrear,
  rotuloDeFormato,
  type AjustesDeRedes,
  type Medio,
} from './redes';

export interface CajaDeRedesProps {
  prompt: string;
  onPromptChange: (v: string) => void;
  onGenerar: () => void;
  /** Qué falta para generar: apaga la flecha. */
  falta?: string;
  generando?: boolean;
  adjuntos: ChatAttachment[];
  onQuitarAdjunto: (id: string) => void;
  /** Abre el selector del Agente en esa pestaña (Producto → productos, Modelo → modelos, Añadir referencia → subidas). */
  onAbrirFuentes: (pestana: IdDeFuente) => void;
  ajustes: AjustesDeRedes;
  onAjustes: (fn: (a: AjustesDeRedes) => AjustesDeRedes) => void;
  /** Sin esto la pastilla "Ayuda de prompt" no se dibuja (el plan no tiene Agente). */
  onAyudaDePrompt?: () => void;
  /**
   * "Mejorar prompt": el escritor del medio activo (el del Creador en video, el de las
   * reglas del agente en imagen) escribe el prompt con lo que hay en la caja y lo reemplaza
   * (ADR-2026-10-04-mejorar-prompt-en-redes,
   * ADR-2026-10-04-el-escritor-de-imagen-usa-las-reglas-del-agente).
   */
  onMejorarPrompt?: () => void;
  /** Animar imagen: el texto se está completando solo con la idea de movimiento sugerida. */
  sugiriendo?: boolean;
  mejorando?: boolean;
  /** Cómo terminó la última mejora: el botón lo muestra un momento (tilde o cruz) y vuelve. */
  resultadoDeMejora?: 'listo' | 'fallo' | null;
  /** Hay una imagen pegada o soltada subiéndose: la tarjeta "Añadir referencia" gira. */
  subiendo?: boolean;
  /**
   * SÓLO PARA CUENTAS DEV (`dev_tester`): fuerza con qué motor de imagen se genera, pisando
   * lo que elegiría el escritor por la tarea (ADR-2026-10-05-forzar-el-modelo-en-redes).
   * Sin `onProveedorForzado` la pastilla no se dibuja, que es lo que ve cualquier cliente.
   */
  proveedorForzado?: ProveedorDeImagen | null;
  onProveedorForzado?: (p: ProveedorDeImagen | null) => void;
  /**
   * La pestaña Asistente (ADR-2026-10-09-asistente-en-crear): lo que se escribe no genera,
   * se le manda al Agente en el chat de la sesión. Sin `onAsistente` la pestaña no se
   * dibuja (el plan no tiene Agente).
   */
  asistente?: boolean;
  onAsistente?: (activo: boolean) => void;
  /**
   * Sólo el Asistente, sin el interruptor Imagen / Video / Asistente: la pantalla del Agente
   * (`/agente`), que es la misma conversación con la misma caja
   * (ADR-2026-10-16-el-agente-se-ve-como-el-asistente). Implica `asistente`.
   */
  sinInterruptor?: boolean;
  /** "Enviar" (o Enter) con la caja en Asistente. */
  onEnviarAlAsistente?: () => void;
  /** En Asistente, "Enviar" se puede tocar sin texto: hay algo que ya es el pedido (un lote de
   *  fotos en la portada de `/agente` — ADR-2026-10-19-review-191-el-lote-en-la-portada). */
  puedeEnviarSinTexto?: boolean;
  /**
   * En Asistente, el Agente está respondiendo: con la caja vacía, Enviar pasa a Detener (con
   * texto sigue siendo Enviar, que encola), el mismo botón que se transforma de la caja del
   * chat (ADR-2026-09-13-un-boton-que-se-transforma). Sin `onDetener`, Enviar como siempre.
   */
  respondiendo?: boolean;
  onDetener?: () => void;
  /**
   * En Asistente, la Inteligencia y el engranaje de la caja del Agente, a la izquierda de la
   * fila, en lugar de Prompts (ADR-2026-10-13-inteligencia-y-ajustes-en-el-asistente). Los arma
   * quien tiene el chat: la caja no lo conoce.
   */
  ajustesDelAsistente?: ReactNode;
  /** Cumbre asomándose detrás de la caja: en la portada sí, sobre los resultados no. */
  conMascota?: boolean;
  /** El usuario está usando la caja (foco o texto): en Asistente, Cumbre asoma y escucha. */
  interactuando?: boolean;
  /**
   * La conversación con Cumbre ya empezó y se ve arriba de la caja: la caja es sólo donde
   * se escribe. Sin el interruptor de medio, sin las sugerencias de abajo y sin Cumbre
   * asomando (ya está en la conversación) — ADR-2026-10-12-la-conversacion-del-asistente-en-crear.
   */
  enConversacion?: boolean;
  /**
   * La caja flota sobre las fotos de la cuadrícula: el interruptor y las sugerencias pierden
   * la transparencia y van con relleno, si no se leían mal sobre una foto
   * (ADR-2026-10-12-sobre-la-cuadricula-el-interruptor-y-las-sugerencias-van-llenos).
   */
  sobreFotos?: boolean;
  /**
   * El rubro de la marca. Elegía el clip de la card "Animar imagen", que se fue de abajo de la
   * caja (ADR-2026-10-13-carrusel-en-lugar-de-animar-debajo-de-la-caja); queda para lo que
   * dependa del rubro.
   */
  rubro?: RubroDeCrear;
  /** Pedido de foco para el textarea (ver `PromptBoxLibre`). */
  pedidoDeFoco?: number;
  className?: string;
}


/**
 * El alto de REPOSO de la caja (px): la fila de tarjetas, unas dos líneas de prompt y la
 * fila de opciones.
 * Lo comparten el marco (como mínimo) y el interruptor (fijo): con un prompt largo el
 * marco crece y el interruptor no.
 */
const ALTO_CAJA = 244;

/** La pastilla de la fila de opciones: `h-9`, redonda, gris claro — la de "Recientes". */
const PASTILLA =
  'inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-neutral-100 px-3.5 text-[13.5px] font-medium text-[#02062BB3] transform transition-transform duration-300 hover:-translate-y-0.5';
const PASTILLA_ABIERTA = 'bg-[#E8EEFF] text-brand-primary hover:bg-[#E8EEFF]';
/** En el teléfono, Configuración (sólo su ícono), Prompts, Mejorar prompt y, a la derecha, la
 *  cantidad o la duración van juntos en UNA pastilla gris (ADR-2026-10-09-asistente-en-crear-7);
 *  el botón de la acción va abajo, a todo el ancho (ADR-2026-10-13-generar-dentro-de-la-caja-en-el-telefono-2). */
const PASTILLA_DEL_GRUPO =
  'inline-flex h-8 w-[30px] shrink-0 items-center justify-center rounded-full text-[#02062BB3] transition-colors active:bg-white';
/** Prompts y Mejorar prompt en el grupo del teléfono: ícono y su rótulo
 *  (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono-2). */
// `flex-1`: con la pastilla a todo el ancho, estas dos se estiran y se reparten lo que sobra
// (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono-3).
const PASTILLA_DEL_GRUPO_CON_ROTULO =
  'inline-flex h-8 min-w-fit flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-full px-1.5 text-[12.5px] font-medium text-[#02062BB3] transition-colors active:bg-white';
/** El lugar del final del grupo del teléfono: la cantidad (Imagen) y la duración (Video) miden
 *  LO MISMO —lo que pide el contador, "− 1 +"—, así al cambiar de pestaña Configuración, Prompts
 *  y Mejorar prompt no se corren. */
const ANCHO_DEL_AJUSTE_EN_EL_GRUPO = 'w-[84px] justify-center';
/** La duración, en el grupo del teléfono. */
const PASTILLA_DEL_GRUPO_TEXTO = `inline-flex h-8 shrink-0 items-center rounded-full ${ANCHO_DEL_AJUSTE_EN_EL_GRUPO} text-[12.5px] font-medium text-[#02062BB3] transition-colors active:bg-white`;

/**
 * Un panel que se abre HACIA ARRIBA desde una pastilla, portaleado al body. Cierra con
 * Escape y con un click afuera (el botón que lo abrió lo cierra por su cuenta).
 */
const PanelSobrePastilla: React.FC<{
  botonRef: RefObject<HTMLButtonElement | null>;
  abierto: boolean;
  onCerrar: () => void;
  ancho: number;
  /**
   * Abre DEBAJO del botón, alineado a su borde derecho. Es para la tarjeta "Modo", que está
   * arriba de la caja: por encima no entraba el menú y la primera opción quedaba fuera de
   * la pantalla. Las pastillas de abajo siguen abriendo por encima.
   */
  haciaAbajo?: boolean;
  children: ReactNode;
}> = ({ botonRef, abierto, onCerrar, ancho, haciaAbajo = false, children }) => {
  const posArriba = usePosicionSobreElBoton(botonRef, abierto && !haciaAbajo, ancho);
  const [posAbajo, setPosAbajo] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => {
    if (!abierto || !haciaAbajo) {
      setPosAbajo(null);
      return;
    }
    const medir = () => {
      const r = botonRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.min(Math.max(8, r.right - ancho), window.innerWidth - ancho - 8);
      setPosAbajo({ left, top: r.bottom + 8 });
    };
    medir();
    window.addEventListener('resize', medir);
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [abierto, haciaAbajo, ancho, botonRef]);
  const pos = haciaAbajo ? posAbajo : posArriba;
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    const alClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || botonRef.current?.contains(t)) return;
      onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alClick);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alClick);
    };
  }, [abierto, onCerrar, botonRef]);
  if (!abierto || !pos) return null;
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      className="animate-mode-swap fixed z-[130] rounded-2xl border border-black/[0.06] bg-white p-1.5 shadow-[0_18px_50px_rgba(2,6,43,0.16)]"
      style={'top' in pos ? { left: pos.left, top: pos.top, width: ancho } : { left: pos.left, bottom: pos.bottom, width: ancho }}
    >
      {children}
    </div>,
    document.body,
  );
};

/** La pastilla de sugerencia, la misma en Asistente, Imagen y Video. */

const ESTILO_DE_MODO: Record<ModoDeCrear, { fondo: string; anillo: string; chip: string; texto: string; tarjeta: string; sugerencia: string }> = {
  // Sin lavanda: los azules de la marca (ADR-2026-10-09-asistente-en-crear).
  automatico: {
    tarjeta: 'bg-[#EEF2FF] ring-1 ring-[#1644EB33]',
    sugerencia: 'bg-[linear-gradient(135deg,#EEF3FF,#EAF8FD)] text-[#1644EB] ring-[#1644EB26] hover:bg-[linear-gradient(135deg,#E2EAFF,#DDF4FC)]',
    fondo: 'bg-[linear-gradient(135deg,#EEF3FF,#EAF8FD)] hover:bg-[linear-gradient(135deg,#E2EAFF,#DDF4FC)]',
    anillo: 'ring-[#1644EB]',
    chip: 'bg-[#1644EB14] text-[#1644EB]',
    texto: 'text-[#1644EB]',
  },
  animar: {
    tarjeta: 'bg-[#FFF1EC] ring-1 ring-[#F2553A40]',
    sugerencia: 'bg-[linear-gradient(135deg,#FFF1EC,#FFF6E9)] text-[#D9431F] ring-[#F2553A2E] hover:bg-[linear-gradient(135deg,#FFE4DA,#FFEFD9)]',
    fondo: 'bg-[linear-gradient(135deg,#FFF1EC,#FFF6E9)] hover:bg-[linear-gradient(135deg,#FFE4DA,#FFEFD9)]',
    anillo: 'ring-[#F2553A]',
    chip: 'bg-[#F2553A17] text-[#D9431F]',
    texto: 'text-[#F2553A]',
  },
};

/**
 * Una card acostada de un modo de Crear, debajo de la caja: su carátula (o su clip) a la
 * izquierda, el nombre y qué hace al lado, y una flecha. En el teléfono, dos por fila y sin
 * la bajada (ADR-2026-10-12-los-modos-de-crear-en-cards-debajo-de-la-caja). `llena`: sobre
 * las fotos de la cuadrícula, blanca entera y con más sombra.
 */
/**
 * Las dos cards de abajo de la caja: Imagen automática y Carrusel de imágenes
 * (ADR-2026-10-13-carrusel-en-lugar-de-animar-debajo-de-la-caja; antes la segunda era Animar
 * imagen). Cada una lleva a la pantalla de su modo (`path` de `MODES`); Automática, si no hay
 * ruta, se elige adentro de la caja como antes.
 */
const TARJETAS_DE_ABAJO: readonly { clave: string; path: string; modo?: ModoDeCrear; portada?: string }[] = [
  { clave: 'automatico', path: 'auto', modo: 'automatico' },
  { clave: 'carrusel', path: 'carrusel', portada: '/images/modo_carrusel/caratula_card.webp' },
];

function TarjetaDeModo({
  modo,
  llena,
  onClick,
}: {
  modo: { nombre: string; bajada: string; portada: string; video?: string };
  llena: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex min-w-0 items-center gap-2.5 rounded-2xl p-1.5 pr-3 text-left ring-1 ring-[#1644EB1F] transform transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-white md:w-[340px] md:gap-3 md:p-2 md:pr-4 ${
        llena ? 'bg-white shadow-[0_8px_22px_-10px_rgba(2,6,43,0.45)]' : 'bg-white/80 shadow-[0_8px_20px_-12px_rgba(22,68,235,0.45)] backdrop-blur-sm'
      }`}
    >
      <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-[#EEF0F5] md:h-14 md:w-14">
        {modo.video ? (
          <video src={modo.video} poster={modo.portada} autoPlay muted loop playsInline preload="metadata" className="h-full w-full object-cover" />
        ) : (
          <img src={modo.portada} alt="" className="h-full w-full object-cover" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold leading-tight text-brand-black md:truncate md:text-[14px]">{modo.nombre}</span>
        <span className="mt-0.5 hidden truncate text-[12.5px] text-[#3C3C43]/60 md:block">{modo.bajada}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-[#02062B]/35 transition-transform duration-300 group-hover:translate-x-0.5 max-md:hidden" strokeWidth={2.25} aria-hidden="true" />
    </button>
  );
}

/** Una tarjeta que el modo elegido no usa: se ve pero no se toca. Sin `si`, no envuelve nada. */
const Atenuada: React.FC<{ si: boolean; motivo: string; children: ReactNode }> = ({ si, motivo, children }) => (
  <div className={si ? 'flex min-w-0 flex-1 opacity-40 pointer-events-none' : 'contents'} aria-disabled={si || undefined} title={si ? motivo : undefined}>
    {children}
  </div>
);

/** Una fila de opción de los paneles: texto a la izquierda, tilde a la derecha. */
const Opcion: React.FC<{ activa: boolean; onClick: () => void; children: ReactNode; detalle?: ReactNode }> = ({ activa, onClick, children, detalle }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${activa ? 'bg-[#1644EB0F]' : 'hover:bg-neutral-50'}`}
  >
    <span className="min-w-0">
      <span className={`block text-[14px] ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{children}</span>
      {detalle && <span className="mt-0.5 block text-[12px] text-[#3C3C43]/55">{detalle}</span>}
    </span>
    {activa && <Check className="h-4 w-4 shrink-0 text-brand-primary" />}
  </button>
);

/** Lo que elige el interruptor: un medio para generar, o hablar con el Asistente. */
type OpcionDelInterruptor = Medio | 'asistente';

/**
 * El interruptor Imagen / Video / Asistente: en VERTICAL a la izquierda de la caja en
 * computadora; en el teléfono, ACOSTADO arriba de la caja (ADR-2026-10-09-asistente-en-crear).
 * Asistente sólo si `conAsistente`.
 */
const ToggleDeMedio: React.FC<{
  value: OpcionDelInterruptor;
  onChange: (m: OpcionDelInterruptor) => void;
  conAsistente: boolean;
  /** Riel con relleno en vez de translúcido: sobre las fotos de la cuadrícula. */
  lleno?: boolean;
}> = ({ value, onChange, conAsistente, lleno = false }) => {
  const esEscritorio = useEsEscritorio();
  const opciones: { id: OpcionDelInterruptor; rotulo: string; Icono: typeof ImageIcon }[] = [
    { id: 'imagen', rotulo: 'Imagen', Icono: ImageIcon },
    { id: 'video', rotulo: 'Video', Icono: Clapperboard },
    ...(conAsistente ? [{ id: 'asistente' as const, rotulo: 'Asistente', Icono: MessageCircle }] : []),
  ];
  const idx = Math.max(0, opciones.findIndex((o) => o.id === value));
  const n = opciones.length;
  return (
    <div
      role="tablist"
      aria-orientation={esEscritorio ? 'vertical' : 'horizontal'}
      aria-label="Qué querés crear"
      // Alto FIJO y alineado abajo: cuando el prompt crece, el marco de al lado se estira
      // y éste se queda en su lugar.
      style={esEscritorio ? { height: ALTO_CAJA } : undefined}
      className={`relative flex h-12 w-full shrink-0 flex-row rounded-full p-1 ring-1 ring-[#1644EB1F] md:h-auto md:w-[78px] md:flex-col md:self-end md:rounded-[20px] ${
        lleno ? 'bg-[#EEF1F8] shadow-[0_8px_22px_-10px_rgba(2,6,43,0.45)]' : 'bg-white/60 backdrop-blur-sm'
      }`}
    >
      {/* El indicador de la opción elegida: una pastilla BLANCA que se desliza sobre el
          riel — el azul de la caja es del Generar, y dos bloques azules a la misma altura
          competían. */}
      <span
        aria-hidden="true"
        className="absolute rounded-full bg-white shadow-[0_2px_10px_rgba(22,68,235,0.14)] transition-transform duration-300 ease-out md:rounded-[16px]"
        style={
          esEscritorio
            ? { left: 4, right: 4, top: 4, height: `calc((100% - 8px) / ${n})`, transform: `translateY(${idx * 100}%)` }
            : { top: 4, bottom: 4, left: 4, width: `calc((100% - 8px) / ${n})`, transform: `translateX(${idx * 100}%)` }
        }
      />
      {opciones.map(({ id, rotulo, Icono }) => {
        const activa = id === value;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activa}
            onClick={() => onChange(id)}
            className={`relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-full text-[13px] font-semibold transition-colors duration-300 md:flex-col md:gap-1 md:rounded-[16px] md:text-[12px] ${
              activa ? (id === 'asistente' ? 'text-brand-primary' : 'text-brand-black') : 'text-[#02062B80] hover:text-brand-black'
            }`}
          >
            <Icono className="h-[17px] w-[17px] md:h-[18px] md:w-[18px]" strokeWidth={2} />
            {rotulo}
            {/* Asistente es nuevo: un punto coral hasta que se lo elige. */}
            {id === 'asistente' && !activa && (
              <span aria-hidden="true" className="absolute right-3 top-2 h-[7px] w-[7px] rounded-full bg-[#FF7A59] ring-2 ring-white md:right-2 md:top-2" />
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Un número que RUEDA al cambiar: el viejo se va hacia arriba y el nuevo entra desde abajo
 * (al bajar, al revés). Es el costo del Generar, que cambia con la cantidad, la calidad y
 * la duración. Las dos animaciones viven en `index.css` (`contador-sale` / `contador-entra`).
 */
const NumeroRodante: React.FC<{ valor: number }> = ({ valor }) => {
  const [estado, setEstado] = useState<{ actual: number; anterior: number | null; dir: 1 | -1 }>({ actual: valor, anterior: null, dir: 1 });
  useEffect(() => {
    if (valor === estado.actual) return;
    setEstado((e) => ({ actual: valor, anterior: e.actual, dir: valor > e.actual ? 1 : -1 }));
    const t = window.setTimeout(() => setEstado((e) => ({ ...e, anterior: null })), 320);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor]);
  const estilo = { '--dir': estado.dir } as React.CSSProperties;
  return (
    <span className="relative inline-block h-[1.25em] min-w-[1ch] overflow-hidden align-middle tabular-nums" style={estilo}>
      {estado.anterior !== null && (
        <span key={`sale-${estado.anterior}-${estado.actual}`} className="absolute inset-x-0 top-0 animate-contador-sale leading-[1.25em]">
          {estado.anterior}
        </span>
      )}
      <span key={`entra-${estado.actual}`} className={`block leading-[1.25em] ${estado.anterior !== null ? 'animate-contador-entra' : ''}`}>
        {estado.actual}
      </span>
    </span>
  );
};

// `TarjetaDeReferencia` vive en su archivo: la comparte la barra de Editar del visor
// (ADR-2026-10-06-editar-con-referencias-y-escritor).

export const CajaDeRedes: React.FC<CajaDeRedesProps> = ({
  prompt,
  onPromptChange,
  onGenerar,
  falta,
  generando = false,
  adjuntos,
  onQuitarAdjunto,
  onAbrirFuentes,
  ajustes,
  onAjustes,
  onAyudaDePrompt,
  onMejorarPrompt,
  sugiriendo = false,
  mejorando = false,
  resultadoDeMejora = null,
  subiendo = false,
  proveedorForzado = null,
  onProveedorForzado,
  asistente = false,
  onAsistente,
  sinInterruptor = false,
  onEnviarAlAsistente,
  puedeEnviarSinTexto = false,
  respondiendo = false,
  onDetener,
  ajustesDelAsistente,
  conMascota = false,
  interactuando = false,
  enConversacion = false,
  sobreFotos = false,
  pedidoDeFoco,
  className = '',
}) => {
  const esVideo = ajustes.medio === 'video';
  // En Asistente la caja no genera: escribe un pedido para el Agente
  // (ADR-2026-10-09-asistente-en-crear). Lo que es de generar (configuración, cantidad,
  // créditos, modos, mejorar el prompt) se va; quedan los adjuntos, el texto y la biblioteca.
  const enAsistente = sinInterruptor || (asistente && !!onAsistente);
  const esEscritorio = useEsEscritorio();
  /** Las opciones agrupadas en una pastilla (teléfono, fuera del Asistente). */
  const enGrupo = !esEscritorio && !enAsistente;
  const raizRef = useRef<HTMLDivElement | null>(null);

  // Los modos adentro de Crear (ADR-2026-10-09-modos-adentro-de-crear).
  const auto = esAutomatico(ajustes);
  const animar = esAnimar(ajustes);
  const modoActual = MODOS_DE_CREAR.find((m) => m.id === ajustes.modo) ?? null;
  const navigate = useNavigate();
  /** Elegir un modo deja la caja lista: pasa al medio del modo y abre lo que le falta. */
  const elegirModo = (id: ModoDeCrear) => {
    onAjustes((a) => conModo(a, id));
    if (id === 'automatico' && !adjuntos.some((a) => a.kind === 'product')) onAbrirFuentes('productos');
    // Animar sin foto: con un producto, la pantalla usa la foto del producto
    // (ADR-2026-10-12-animar-usa-la-foto-del-producto); sin nada, se elige una.
    if (id === 'animar' && !adjuntos.some((a) => a.kind === 'image' || a.kind === 'product')) onAbrirFuentes('subidas');
  };
  const creditos = creditosDeRedes(ajustes, adjuntos);
  const userProducts = useUserProducts();
  const userModels = useUserModels();
  // Producto, modelo e imágenes subidas se ven en las tarjetas de arriba; lo demás (poses,
  // fondos, creaciones del selector) sigue en los chips de la caja.
  const productos = adjuntos.filter((a) => a.kind === 'product');
  const modelos = adjuntos.filter((a) => a.kind === 'model');
  // Un video o una referencia (pose, fondo, foto de moodboard) sumada va con las imágenes, en
  // "Añadir referencia". También la carpeta de moodboard entera, que es una referencia de estilo
  // (ADR-2026-10-18-el-moodboard-en-el-selector-de-fuentes-2).
  const esReferencia = (a: ChatAttachment) =>
    a.kind === 'image' || a.kind === 'video' || a.kind === 'referencia' || a.kind === 'moodboard';
  const subidas = adjuntos.filter(esReferencia);
  // El video de "Añadir referencia" se toca para verlo
  // (ADR-2026-10-20-el-video-adjunto-se-ve-y-se-reproduce).
  const { ver: verVideo, visor: visorDeVideo } = useVerVideoAdjunto();
  const otros = adjuntos.filter((a) => a.kind !== 'product' && a.kind !== 'model' && !esReferencia(a));
  const [panel, setPanel] = useState<'configuracion' | 'tiempo' | 'modelo' | null>(null);
  const refModelo = useRef<HTMLButtonElement | null>(null);
  const [biblioteca, setBiblioteca] = useState(false);
  const refConfig = useRef<HTMLButtonElement | null>(null);
  const refTiempo = useRef<HTMLButtonElement | null>(null);
  const cerrar = () => setPanel(null);
  // La marca de estado de "Mejorar prompt" (ADR-2026-10-04-status-mark-en-mejorar-prompt):
  // al tocar, nace como anillo de rayitas y un cuadro después pasa a "en curso", así se ve
  // la fusión en el arco que gira; al terminar, el tilde (o la cruz) un momento.
  const [arcoArrancado, setArcoArrancado] = useState(false);
  useEffect(() => {
    if (!mejorando) {
      setArcoArrancado(false);
      return;
    }
    const id = window.requestAnimationFrame(() => setArcoArrancado(true));
    return () => window.cancelAnimationFrame(id);
  }, [mejorando]);
  // Cuando la mejora termina bien, el texto nuevo de la caja hace un rebote sutil.
  const [pulsoDeTexto, setPulsoDeTexto] = useState(0);
  useEffect(() => {
    if (resultadoDeMejora === 'listo') setPulsoDeTexto((p) => p + 1);
  }, [resultadoDeMejora]);
  const marcaDeMejora: EstadoDeMarca | null = mejorando
    ? arcoArrancado
      ? 'running'
      : 'pending'
    : resultadoDeMejora === 'listo'
      ? 'done'
      : resultadoDeMejora === 'fallo'
        ? 'failed'
        : null;
  const alternar = (p: NonNullable<typeof panel>) => setPanel((v) => (v === p ? null : p));

  const setImagen = (fn: (i: AjustesDeRedes['imagen']) => AjustesDeRedes['imagen']) =>
    onAjustes((a) => ({ ...a, imagen: fn(a.imagen) }));
  const setVideo = (fn: (v: AjustesDeRedes['video']) => AjustesDeRedes['video']) =>
    onAjustes((a) => ({ ...a, video: fn(a.video) }));
  const setFormato = (slug: string) =>
    onAjustes((a) => (a.medio === 'video' ? { ...a, video: { ...a.video, formatSlug: slug } } : { ...a, imagen: { ...a.imagen, formatSlug: slug } }));

  const formato = formatoActivo(ajustes);
  // El registro de formatos personalizados, cargado aunque no se abra la Configuración: el
  // pedido lleva la copia del formato elegido (ADR-2026-10-20-formatos-personalizados).
  useFormatosPersonalizados();
  const aj = ajustes.video.ajustes;
  // Animar imagen va de 4 a 15 s, como el modo; el resto del video, la escala de su calidad.
  const duracionMin = animar ? ANIMAR_MIN_SEG : VIDEO_DURATION_MIN;
  const duracionMax = animar ? ANIMAR_MAX_SEG : videoDuracionMax(aj.quality);
  const modeloCredito = aj.quality === 'profesional' ? 'pro' : 'standard';

  /** La duración (video) o la cantidad (imagen): van a la DERECHA, al lado del Generar. */
  const ajusteDelMedio = esVideo ? (
    <>

        <>
          <button ref={refTiempo} type="button" onClick={() => alternar('tiempo')} aria-expanded={panel === 'tiempo'} className={`${enGrupo ? PASTILLA_DEL_GRUPO_TEXTO : PASTILLA} tabular-nums ${panel === 'tiempo' ? PASTILLA_ABIERTA : ''}`}>
            {animar ? duracionDeAnimar(aj.durationSeconds) : aj.durationSeconds} seg
          </button>
          <PanelSobrePastilla botonRef={refTiempo} abierto={panel === 'tiempo'} onCerrar={cerrar} ancho={280}>
            <div className="px-3 py-2.5">
              <div className="mb-2.5 flex items-center justify-between gap-3">
                <span className="text-[14px] text-[#02062BE6]">Duración</span>
                <span className="text-[13.5px] font-semibold tabular-nums text-brand-primary">{animar ? duracionDeAnimar(aj.durationSeconds) : aj.durationSeconds} seg</span>
              </div>
              {/* El slider de barras con estela (ADR-2026-10-03-wake-slider). Cantidad de
                  barras FIJA: una por segundo dejaba 11 barras gordas en Básica (5–15 s) y 26
                  finitas en Profesional; con 28 se ve igual en las dos. */}
              <WakeSlider
                value={animar ? duracionDeAnimar(aj.durationSeconds) : aj.durationSeconds}
                min={duracionMin}
                max={duracionMax}
                step={1}
                bars={28}
                height={40}
                restHeight={12}
                gap={3}
                onChange={(n) => setVideo((v) => ({ ...v, ajustes: { ...v.ajustes, durationSeconds: n } }))}
                ariaLabel="Duración del video en segundos"
                formatValue={(n) => `${n} segundos`}
              />
              <div className="mt-1 flex items-center justify-between text-[11px] tabular-nums text-[#3C3C43]/45">
                <span>{duracionMin} seg</span>
                <span>{duracionMax} seg</span>
              </div>
            </div>
          </PanelSobrePastilla>
        </>
    </>
  ) : (
        /* Cantidad: el contador vive en la pastilla, no en un panel — son dos toques. */
        <span className={enGrupo ? `inline-flex h-8 shrink-0 items-center gap-1 ${ANCHO_DEL_AJUSTE_EN_EL_GRUPO}` : `${PASTILLA} gap-1 pl-1.5 pr-1.5`} aria-label="Cantidad de imágenes">
          <button
            type="button"
            aria-label="Menos"
            disabled={ajustes.imagen.cantidad <= 1}
            onClick={() => setImagen((im) => ({ ...im, cantidad: Math.max(1, im.cantidad - 1) }))}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <span className="w-5 text-center text-[13.5px] font-semibold tabular-nums text-[#02062BE6]">{ajustes.imagen.cantidad}</span>
          <button
            type="button"
            aria-label="Más"
            disabled={ajustes.imagen.cantidad >= MAX_CANTIDAD}
            onClick={() => setImagen((im) => ({ ...im, cantidad: Math.min(MAX_CANTIDAD, im.cantidad + 1) }))}
            className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-white disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </span>
  );

  const pastillas = (
    <>
      {enAsistente && ajustesDelAsistente}

      {/* Una sola pastilla de Configuración por medio: Formato + Calidad en imagen; Formato +
          Calidad + Resolución en video (el tiempo tiene la suya, al lado). El rótulo resume
          dice "Configuración"; lo elegido se ve al abrirla. */}
      {/* En Animar no hay nada que configurar: sale en 9:16 y la duración tiene su pastilla.
          En Asistente tampoco: el formato y la calidad los propone el Agente. */}
      {!animar && !enAsistente && (
      <button ref={refConfig} type="button" onClick={() => alternar('configuracion')} aria-expanded={panel === 'configuracion'} aria-label={enGrupo ? 'Configuración' : undefined} className={`${enGrupo ? PASTILLA_DEL_GRUPO : PASTILLA} ${panel === 'configuracion' ? PASTILLA_ABIERTA : ''}`}>
        <Settings2 className="h-4 w-4" strokeWidth={2} />
        {!enGrupo && 'Configuración'}
      </button>
      )}
      <PanelSobrePastilla botonRef={refConfig} abierto={panel === 'configuracion'} onCerrar={cerrar} ancho={320}>
        <div className="px-3 pb-1 pt-2.5">
          <span className="mb-2 block text-[14px] text-[#02062BE6]">Formato</span>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Formato">
            {FORMAT_OPTIONS_V2.map((f) => {
              const activa = f.id === formato;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="radio"
                  aria-checked={activa}
                  onClick={() => setFormato(f.id)}
                  className={`rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors ${activa ? 'bg-[#1644EB0F] text-brand-primary ring-1 ring-brand-primary' : 'bg-neutral-50 text-[#02062BE6] ring-1 ring-transparent hover:bg-neutral-100'}`}
                >
                  {rotuloDeFormato(f.id)}
                </button>
              );
            })}
          </div>
          {/* Los formatos personalizados de la marca, sólo en imagen: el recorte y la
              conversión al descargar no corren sobre un video
              (ADR-2026-10-20-formatos-personalizados). */}
          {!esVideo && <FormatosPersonalizados selectedId={formato} onPick={setFormato} layout="chips" />}
        </div>
        {esVideo ? (
          <>
            <div className="px-3 pb-1 pt-3">
              <span className="mb-2 block text-[14px] text-[#02062BE6]">Calidad</span>
              <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Calidad del video">
                {([
                  { id: 'profesional', label: 'Profesional', sub: 'hasta 30s' },
                  { id: 'basica', label: 'Básica', sub: 'hasta 15s' },
                ] as { id: VideoQuality; label: string; sub: string }[]).map((opt) => {
                  const activa = opt.id === aj.quality;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      onClick={() => setVideo((v) => ({ ...v, ajustes: conCalidad(v.ajustes, opt.id) }))}
                      className={`rounded-xl px-2 py-2 text-center transition-colors ${activa ? 'bg-[#1644EB0F] ring-1 ring-brand-primary' : 'bg-neutral-50 ring-1 ring-transparent hover:bg-neutral-100'}`}
                    >
                      <span className={`block text-[13.5px] font-medium ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{opt.label}</span>
                      <span className="mt-0.5 block text-[11px] text-[#3C3C43]/55">{opt.sub}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-3 pb-2.5 pt-3">
              <span className="mb-2 block text-[14px] text-[#02062BE6]">Resolución</span>
              <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Resolución del video">
                {RESOLUCIONES_VIDEO.map((res) => {
                  const activa = res === aj.resolution;
                  return (
                    <button
                      key={res}
                      type="button"
                      role="radio"
                      aria-checked={activa}
                      title={esBorrador(aj.quality, res) ? AYUDA_BORRADOR : undefined}
                      onClick={() => setVideo((v) => ({ ...v, ajustes: { ...v.ajustes, resolution: res as VideoResolution } }))}
                      className={`rounded-xl px-1.5 py-2 text-center transition-colors ${activa ? 'bg-[#1644EB0F] ring-1 ring-brand-primary' : 'bg-neutral-50 ring-1 ring-transparent hover:bg-neutral-100'}`}
                    >
                      <span className={`block text-[13px] font-medium tabular-nums ${activa ? 'text-brand-primary' : 'text-[#02062BE6]'}`}>{res}</span>
                      <span className="mt-0.5 block text-[10.5px] tabular-nums text-[#3C3C43]/55">
                        {videoGeneralRatePerSec(modeloCredito, res as VideoResolution)} cr/s
                      </span>
                      {esBorrador(aj.quality, res) && <EtiquetaBorrador compacta className="mt-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        ) : (
          <div className="px-3 pb-2.5 pt-3">
            <span className="mb-1 block text-[14px] text-[#02062BE6]">Calidad</span>
            {OPCIONES_RESOLUCION.map((o) => (
              <Opcion
                key={o.id}
                activa={o.id === ajustes.imagen.calidad}
                onClick={() => setImagen((im) => ({ ...im, calidad: o.id as ResolucionImagen }))}
                detalle={`${o.detalle} ${creditosPorImagenTexto(o.id)}.`}
              >
                {o.label}
              </Opcion>
            ))}
          </div>
        )}
      </PanelSobrePastilla>

      {/* La Biblioteca de prompts reemplaza a "Recientes": el historial sigue adentro,
          junto con favoritos y los catálogos de fragmentos (ADR-2026-10-02-biblioteca-de-prompts).
          En Asistente no: ahí la fila es la de la caja del Agente (Inteligencia y ajustes). */}
      {!enAsistente && (
      <button type="button" onClick={() => setBiblioteca(true)} aria-haspopup="dialog" className={enGrupo ? PASTILLA_DEL_GRUPO_CON_ROTULO : PASTILLA}>
        <BookOpen className="h-4 w-4" strokeWidth={2} />
        Prompts
      </button>
      )}

      {onAyudaDePrompt && (
        <button type="button" onClick={onAyudaDePrompt} className={PASTILLA}>
          <Sparkles className="h-4 w-4" strokeWidth={2} />
          Ayuda de prompt
        </button>
      )}

      {/* ── SÓLO DEV: forzar el motor de imagen ──────────────────────────────────────
          Pastilla con borde punteado ámbar y la marca DEV, a propósito distinta de las
          demás: no es parte del producto, es la tuerca para probar un motor. Sólo en
          Imagen (el video no tiene motor que forzar: lo elige la calidad). */}
      {onProveedorForzado && !esVideo && !enAsistente && (
        <>
          <button
            ref={refModelo}
            type="button"
            onClick={() => alternar('modelo')}
            aria-expanded={panel === 'modelo'}
            title="Sólo cuentas dev: fuerza el motor de imagen"
            className={`inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed px-3 text-[13px] font-medium transform transition-transform duration-300 hover:-translate-y-0.5 ${
              proveedorForzado ? 'border-[#B45309] bg-[#FEF3C7] text-[#92400E]' : 'border-[#D97706]/60 bg-[#FFFBEB] text-[#92400E]/80'
            }`}
          >
            <span className="rounded bg-[#B45309] px-1 py-px text-[9.5px] font-bold tracking-wide text-white">DEV</span>
            <FlaskConical className="h-3.5 w-3.5" strokeWidth={2.25} />
            {proveedorForzado ? etiquetaProveedorDeImagen(proveedorForzado) : 'Modelo: Atlas elige'}
          </button>
          <PanelSobrePastilla botonRef={refModelo} abierto={panel === 'modelo'} onCerrar={cerrar} ancho={340}>
            <div className="px-3 pb-2.5 pt-3">
              <span className="mb-1 block text-[14px] text-[#02062BE6]">Forzar el motor de imagen</span>
              <span className="mb-2 block text-[12px] text-[#3C3C43]/55">
                Sólo cuentas dev. Elegido corre SOLO, sin fallback, y pisa lo que elegiría el escritor.
              </span>
              <Opcion
                activa={!proveedorForzado}
                onClick={() => {
                  onProveedorForzado(null);
                  cerrar();
                }}
                detalle="El escritor declara la tarea y el backend elige la cadena."
              >
                Atlas elige
              </Opcion>
              {PROVEEDORES_DE_IMAGEN.map((p) => (
                <Opcion
                  key={p.id}
                  activa={proveedorForzado === p.id}
                  onClick={() => {
                    onProveedorForzado(p.id);
                    cerrar();
                  }}
                  detalle={p.detalle}
                >
                  {p.label}
                </Opcion>
              ))}
            </div>
          </PanelSobrePastilla>
        </>
      )}

      {/* Mejorar prompt: el escritor necesita algo de qué partir (texto o una referencia). */}
      {/* Con un modo no hay prompt que mejorar: en Imagen automática la escena la escribe el
          director de arte y en Animar el texto ya se completa con una sugerencia. */}
      {/* En Asistente tampoco: el pedido es para Cumbre, que lo entiende como está. */}
      {onMejorarPrompt && !auto && !animar && !enAsistente && (
        <button
          type="button"
          onClick={onMejorarPrompt}
          disabled={mejorando || generando || (!prompt.trim() && adjuntos.length === 0)}
          aria-busy={mejorando || undefined}
          // Mientras mejora NO se apaga con `disabled:opacity-50`: el botón es la barra de
          // carga y tiene que leerse entero.
          className={`${enGrupo ? PASTILLA_DEL_GRUPO_CON_ROTULO : PASTILLA} relative overflow-hidden ${mejorando ? '' : 'disabled:opacity-50'} disabled:hover:translate-y-0`}
        >
          {/* El fondo del botón como barra de carga: un tono un poco más oscuro que avanza
              lo que suele tardar la mejora (la media medida, por medio). Va hasta el 96 % en
              el DOBLE de esa media con una curva que frena, así a la media ya llenó casi
              todo y si tarda más sigue avanzando despacio en vez de quedarse clavada; al
              volver la respuesta completa de golpe y se va con el tilde. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 bg-[#02062B]/[0.09]"
            style={{
              width: mejorando ? (arcoArrancado ? '96%' : '0%') : resultadoDeMejora === 'listo' ? '100%' : '0%',
              opacity: mejorando || resultadoDeMejora === 'listo' ? 1 : 0,
              transition: mejorando
                ? arcoArrancado
                  ? `width ${DURACION_MEDIA_DE_MEJORA_MS[ajustes.medio] * 2}ms cubic-bezier(0.15, 0.6, 0.3, 1)`
                  : 'none'
                : resultadoDeMejora === 'listo'
                  ? 'width 220ms ease-out'
                  : 'opacity 300ms ease, width 0ms linear 300ms',
            }}
          />
          <span className="relative inline-flex items-center gap-1.5">
            {marcaDeMejora ? (
              <StatusMark status={marcaDeMejora} size={16} strokeWidth={2.25} doneColor="#16A34A" errorColor="#DC2626" />
            ) : (
              <Wand2 className="h-4 w-4" strokeWidth={2} />
            )}
            {mejorando ? (
              'Mejorando…'
            ) : resultadoDeMejora === 'listo' ? (
              'Mejorado'
            ) : resultadoDeMejora === 'fallo' ? (
              'No se pudo'
            ) : enGrupo ? (
              // En un teléfono de menos de 380 px no entra completo: "Mejorar".
              <span className="whitespace-nowrap">
                Mejorar<span className="max-[379px]:hidden"> prompt</span>
              </span>
            ) : (
              'Mejorar prompt'
            )}
          </span>
        </button>
      )}
    </>
  );

  /**
   * El botón de la acción: Enviar en el Asistente (sin créditos: el pedido va al chat y Cumbre
   * cobra recién cuando genera) o Generar con su costo. `ancho`: el del teléfono, debajo de la
   * caja y a todo el ancho (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono).
   */
  const botonDeAccion = (ancho: boolean) => {
    const forma = ancho
      ? 'flex h-10 w-full justify-center px-5 text-[14.5px]'
      : `inline-flex h-10 shrink-0 ${enAsistente ? 'pl-5 pr-4' : 'px-4 md:px-5'} text-[14px]`;
    const clase = `group relative ${forma} items-center gap-2 overflow-hidden rounded-full bg-[#C9D2F3] py-2 font-semibold text-white shadow-[0_10px_24px_-10px_rgba(22,68,235,0.65)] transform transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:shadow-[0_10px_24px_-10px_rgba(22,68,235,0)] disabled:hover:translate-y-0`;
    // Prender y apagar es un FUNDIDO: el fondo de abajo es el azul claro de apagado y el
    // degradé va en una capa encima que aparece o se va con la opacidad (un `background-image`
    // no se puede transicionar). La sombra acompaña.
    const degrade = (
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(181.87deg,#2563F5_8.76%,#0436AE_90.56%)] opacity-100 transition-opacity duration-300 ease-out group-disabled:opacity-0"
      />
    );
    if (enAsistente && respondiendo && onDetener && !prompt.trim()) {
      return (
        <button type="button" onClick={onDetener} aria-label="Detener" className={`${forma} items-center gap-2 rounded-full bg-brand-black py-2 font-semibold text-white transition-opacity hover:opacity-90`}>
          <span>Detener</span>
          <Square className="h-3 w-3 fill-current" aria-hidden="true" />
        </button>
      );
    }
    if (enAsistente) {
      return (
        <button type="button" onClick={() => onEnviarAlAsistente?.()} disabled={(!prompt.trim() && !puedeEnviarSinTexto) || generando} className={clase}>
          {degrade}
          <span aria-hidden="true" className="crear-brillo group-disabled:hidden" />
          <span className="relative">Enviar</span>
          <ArrowUp className="relative h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
        </button>
      );
    }
    return (
      <button type="button" onClick={onGenerar} disabled={!!falta || generando} title={falta} className={clase}>
        {degrade}
        {/* El brillo que cruza el botón cada tanto, sólo prendido. */}
        <span aria-hidden="true" className="crear-brillo group-disabled:hidden" />
        <span className="relative">Generar</span>
        {/* El costo con el MISMO ícono de créditos de toda la app, en su pastilla blanca; apagado
            el botón, la pastilla se apaga con él. */}
        <span className="relative inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[12.5px] font-semibold tabular-nums text-brand-black transition-opacity duration-300 ease-out group-disabled:opacity-50">
          <img src="/images/token_icon.png" alt="" aria-hidden="true" className="h-3.5 w-3.5 object-contain" />
          <NumeroRodante valor={creditos} />
        </span>
      </button>
    );
  };

  // Cumbre asoma detrás de la caja: en Imagen y Video siempre (ofrece ayuda y lleva al
  // Asistente); en el Asistente, cuando el usuario empieza a escribir, a escuchar.
  const mascotaVisible = conMascota && (!enAsistente || interactuando);
  const globitoDeMascota = enAsistente
    ? { texto: 'Te escucho…', fijo: true }
    : onAsistente
      ? { texto: '¿Te ayudo con la idea?', onClick: () => onAsistente(true) }
      : undefined;

  return (
    <div ref={raizRef} className={className}>
      <BibliotecaDePrompts
        abierto={biblioteca}
        onCerrar={() => setBiblioteca(false)}
        prompt={prompt}
        onPromptChange={onPromptChange}
        tipoDeHistorial={esVideo ? 'video' : 'prompt_libre'}
      />
      {/* El interruptor y la caja: lado a lado en computadora; en el teléfono el interruptor
          va acostado ARRIBA (con aire para que Cumbre se asome entre los dos). */}
      {/* Sobre la cuadrícula, en el teléfono el interruptor y la caja van juntos en una card
          blanca: los separa de las fotos de atrás
          (ADR-2026-10-12-la-caja-sobre-los-resultados-en-el-telefono). */}
      <div
        className={`flex flex-col gap-3 md:flex-row md:items-end ${conMascota ? 'max-md:gap-10' : ''} ${
          sobreFotos
            ? 'max-md:gap-1.5 max-md:rounded-[26px] max-md:bg-white max-md:p-1.5 max-md:shadow-[0_-12px_40px_-14px_rgba(2,6,43,0.35)] max-md:ring-1 max-md:ring-black/[0.05]'
            : ''
        }`}
      >
      {!enConversacion && !sinInterruptor && (
      <ToggleDeMedio
        value={enAsistente ? 'asistente' : ajustes.medio}
        conAsistente={!!onAsistente}
        lleno={sobreFotos}
        onChange={(opcion) => {
          if (opcion === 'asistente') {
            onAsistente?.(true);
            return;
          }
          onAsistente?.(false);
          onAjustes((a) => conMedio(a, opcion));
        }}
      />
      )}
      <div className="relative min-w-0 flex-1">
      {conMascota && !enConversacion && (
        <CumbreQueAsoma
          visible={mascotaVisible}
          globito={globitoDeMascota}
          // En el Asistente, en computadora no asoma: el "Te escucho…" sobre una caja que ya
          // es para escribir era ruido (ADR-2026-10-20-en-el-asistente-cumbre-no-asoma-en-computadora).
          // En el teléfono sigue.
          className={`absolute -top-[33px] right-5 z-0 md:-top-[58px] md:right-14 ${enAsistente ? 'md:hidden' : ''}`}
        />
      )}
      <div
        // Sin `overflow-hidden`: las fotos de las carpetitas de arriba salen por encima
        // del botón (y del marco) al abrirse, y el recorte las cortaba.
        className="relative z-10 flex min-w-0 flex-1 rounded-[22px] bg-white shadow-[0_30px_70px_-26px_rgba(22,68,235,0.35),0_2px_6px_rgba(2,6,43,0.04)] ring-1 ring-[#1644EB14] transition-shadow focus-within:ring-[#1644EB40] md:min-h-[244px]"
      >
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Las tarjetas de referencia, ADENTRO del marco y arriba del prompt: las tres
              se reparten el ancho en partes iguales. Cada una en su azul. */}
          <div className="flex gap-1.5 px-1.5 pt-2 md:gap-2 md:px-3 md:pt-3">
            {/* Cada modo atenúa lo que no usa (Imagen automática: modelo y referencias; Animar:
                producto y modelo). Sin modo —o en Asistente, que usa todo—, `contents` deja el
                reparto del ancho como siempre. */}
            <Atenuada si={animar && !enAsistente} motivo="Animar usa sólo la foto de Añadir referencia">
            <TarjetaDeReferencia
              rotulo="Añadir producto"
              Icono={Package}
              adjuntos={productos}
              carpeta={{ imagenes: userProducts.map((p) => p.imageUrl).filter(Boolean), tono: 'azul' }}
              fondo="bg-[#EEF3FF]"
              compactaEnTelefono
              onAbrir={() => onAbrirFuentes('productos')}
              onQuitar={() => productos.forEach((a) => onQuitarAdjunto(a.id))}
            />
            </Atenuada>
            <Atenuada si={(auto || animar) && !enAsistente} motivo={auto ? 'En Imagen automática, Atlas elige el modelo' : 'Animar usa sólo la foto de Añadir referencia'}>
            <TarjetaDeReferencia
              rotulo={`Añadir ${palabraDeModeloActual().singular}`}
              Icono={User}
              adjuntos={modelos}
              carpeta={{ imagenes: userModels.map((m) => m.thumbnailUrl ?? '').filter(Boolean), tono: 'celeste' }}
              fondo="bg-[#ECF5FF]"
              compactaEnTelefono
              onAbrir={() => onAbrirFuentes('modelos')}
              onQuitar={() => modelos.forEach((a) => onQuitarAdjunto(a.id))}
            />
            </Atenuada>
            <Atenuada si={auto && !enAsistente} motivo="En Imagen automática, Atlas elige la escena">
            <TarjetaDeReferencia
              rotulo="Añadir referencia"
              esDestino
              Icono={Upload}
              adjuntos={subidas}
              // Pegar o soltar una imagen la sube acá: mientras viaja, la tarjeta lo dice.
              estado={subiendo ? 'Subiendo…' : undefined}
              carpeta={{
                imagenes: [],
                tono: 'cian',
                relleno: subiendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} /> : <Upload className="h-3.5 w-3.5" strokeWidth={2.25} />,
              }}
              fondo="bg-[#EAFAFC]"
              compactaEnTelefono
              // Abre en "Subir archivo"; Estilos queda a una pestaña
              // (ADR-2026-10-19-anadir-referencia-abre-en-subir-archivo).
              onAbrir={() => onAbrirFuentes('subidas')}
              onQuitar={() => subidas.forEach((a) => onQuitarAdjunto(a.id))}
              onVerVideo={(v) => void verVideo(v)}
            />
            </Atenuada>
          </div>
          {/* Por qué el video de referencia no sirve (o sirve con reparos)
              (ADR-2026-10-20-el-video-de-referencia-se-ajusta-a-lo-que-acepta-seedance). */}
          <AvisosDeVideos adjuntos={subidas} className="px-1.5 pt-2 md:px-3" />
          {visorDeVideo}
          <div className="min-h-0 flex-1">
          <PromptBoxLibre
            marco="ninguno"
            micAntesDeDerecha
            // En Imagen y Video del teléfono, sin el mic: no entra, y el teclado ya dicta. En el
            // Asistente sí, como en la computadora: ahí la fila tiene lugar
            // (ADR-2026-10-21-la-caja-del-asistente-en-el-telefono).
            sinMicEnTelefono={!enAsistente}
            // En Imagen y Video del teléfono, dos filas adentro de la caja: arriba las
            // herramientas, abajo la cantidad (o la duración) y el botón de la acción a todo el
            // ancho (ADR-2026-10-13-generar-dentro-de-la-caja-en-el-telefono). En el Asistente,
            // una sola fila como en la computadora: Inteligencia y engranaje a la izquierda, mic
            // y Enviar a la derecha.
            filaEnDosEnTelefono={!enAsistente}
            // La caja, más alta en el teléfono: más lugar para escribir.
            textoAltoEnTelefono
            prompt={prompt}
            onPromptChange={onPromptChange}
            onSubmit={enAsistente ? () => onEnviarAlAsistente?.() : onGenerar}
            menusDeTexto
            mencionables={adjuntos}
            reboteAlAgregar
            pulsoDeTexto={pulsoDeTexto}
            pedidoDeFoco={pedidoDeFoco}
            adjuntos={otros}
            onQuitarAdjunto={onQuitarAdjunto}
            conTipo
            disabled={generando}
            placeholder={enAsistente ? 'Contale al Asistente qué querés crear…' : placeholderDeRedes(ajustes)}
            // Al cambiar de pestaña, lo que cambia en la fila entra con un fundido corto.
            izquierda={
              enGrupo ? (
                // Sin el botón al lado (va debajo de la caja): a todo el ancho, con Prompts y
                // Mejorar estirándose para repartir lo que sobra; Configuración y la cantidad miden
                // lo suyo (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono-3). `min-w-max`:
                // en un teléfono angosto no se aprieta, se corre como antes.
                <span key="grupo" className="crear-cambia flex w-full min-w-max items-center rounded-full bg-neutral-100 p-0.5">
                  {pastillas}
                  <span aria-hidden="true" className="mx-0.5 h-4 w-px shrink-0 bg-[#02062B]/15" />
                  {ajusteDelMedio}
                </span>
              ) : (
                <span key={enAsistente ? 'asistente' : 'crear'} className="crear-cambia flex shrink-0 items-center gap-2">
                  {pastillas}
                </span>
              )
            }
            // En el teléfono el botón de la acción va DEBAJO de la caja, a todo el ancho, y la fila
            // queda entera para las herramientas (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono).
            derecha={
              !esEscritorio && !enAsistente ? (
                <span key={enAsistente ? 'asistente' : 'crear'} className="crear-cambia flex w-full items-center">
                  {botonDeAccion(true)}
                </span>
              ) : enAsistente ? (
                <span key="asistente" className="crear-cambia flex items-center">
                  {botonDeAccion(false)}
                </span>
              ) : (
                <span key="crear" className="crear-cambia flex items-center gap-2">
                  {!enGrupo && ajusteDelMedio}
                  {botonDeAccion(false)}
                </span>
              )
            }
            // Sólo con un tipo elegido: `PromptBoxLibre` dibuja la fila de chips si hay
            // `anclado`, aunque el componente no pinte nada (en General), y esa fila vacía
            // hacía más alta la caja en Video que en Imagen.
            anclado={
              enAsistente ? undefined : esVideo && ajustes.video.tipoVideo !== 'general' ? (
                <TipoDeVideoAnclado valor={ajustes.video.tipoVideo} onChange={(slug) => setVideo((v) => ({ ...v, tipoVideo: slug }))} />
              ) : (auto || animar) && modoActual ? (
                // El modo elegido, con su X para volver a Crear de siempre, y lo que va a pasar.
                <span className="inline-flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full py-1 pl-1 pr-1 text-[12.5px] font-semibold ${ESTILO_DE_MODO[modoActual.id].chip}`}>
                  <img src={modoActual.portada} alt="" className="h-5 w-5 rounded-full object-cover" />
                  {modoActual.nombre}
                  <button
                    type="button"
                    onClick={() => onAjustes((a) => conModo(a, undefined))}
                    aria-label="Quitar el modo"
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-black/[0.08]"
                  >
                    <X className="h-3 w-3" strokeWidth={2.75} />
                  </button>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#3C3C43]/70">
                  {sugiriendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.25} /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={2.25} />}
                  {auto && sugiriendo
                    ? 'Escribiendo una escena para tu producto…'
                    : auto
                    ? ajustes.imagen.cantidad > 1
                      ? `Atlas arma ${ajustes.imagen.cantidad} escenas distintas con tu producto`
                      : 'Atlas arma la escena con tu producto'
                    : sugiriendo
                      ? 'Escribiendo una idea de movimiento para tu foto…'
                      : subidas.some((a) => a.kind === 'image')
                        ? `Atlas anima tu foto en un video de ${duracionDeAnimar(aj.durationSeconds)} segundos`
                        : 'Elegí la foto a animar en Añadir referencia'}
                </span>
                </span>
              ) : undefined
            }
          />
          </div>
        </div>
      </div>
      </div>
      </div>
      {/* Debajo de la caja, en las TRES pestañas, dos cards acostadas: Imagen automática y
          Carrusel de imágenes (antes Animar imagen), para llegar rápido
          (ADR-2026-10-12-los-modos-de-crear-en-cards-debajo-de-la-caja; antes eran pastillas
          de sugerencia). Centradas con la caja (el `md:ml-[90px]` = el interruptor de 78 +
          el gap). Un toque deja la caja lista para el modo —desde el Asistente, vuelve a
          Imagen o Video— y con un modo elegido se van. */}
      {/* Sobre la cuadrícula, en el teléfono no van: tapaban las fotos. */}
      {/* En el teléfono no van: ahí abajo de la caja va el botón de la acción
          (ADR-2026-10-13-generar-debajo-de-la-caja-en-el-telefono). */}
      {esEscritorio && !enConversacion && !(modoActual && !enAsistente) && (
        <div key="modos" className={`crear-cambia mt-3 grid grid-cols-2 gap-2 md:flex md:justify-center md:gap-3 ${sinInterruptor ? '' : 'md:ml-[90px]'}`}>
          {TARJETAS_DE_ABAJO.map((t) => {
            const deCrear = t.modo ? MODOS_DE_CREAR.find((m) => m.id === t.modo) : undefined;
            return (
              <TarjetaDeModo
                key={t.clave}
                modo={
                  deCrear ?? {
                    nombre: nombreCortoModo(t.path),
                    bajada: bajadaCortaModo(t.path, ''),
                    portada: t.portada ?? '',
                  }
                }
                llena={sobreFotos}
                // Llevan a la pantalla de su modo, afuera de Crear y del Asistente
                // (ADR-2026-10-12-las-cards-de-modo-de-crear-llevan-al-modo). Elegirlo adentro
                // de la caja sigue en la tarjeta "Modo" de arriba.
                onClick={() => {
                  const ruta = rutaDeModo(t.path);
                  if (ruta) {
                    navigate(ruta);
                    return;
                  }
                  if (!t.modo) return;
                  if (enAsistente) onAsistente?.(false);
                  elegirModo(t.modo);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CajaDeRedes;
